import React, { useEffect, useState } from 'react';
import TableHeader from '../data-tables/TableHeader';
import TableToolbar from '../data-tables/TableToolbar';
import TaskDataTable from '../data-tables/TaskDataTable';
import AddTaskModal from '../add-task-modal/AddTaskModal';
import { useAppSelector } from '../../hooks/storeHooks';
import { deleteTask, fetchTaskData, markAsCompleteTaskSave } from '../../services/dashboard/TaskDetailsService';
import { TaskData } from './TaskDataModels';
import * as XLSX from 'xlsx';
import Modal from '../../features/common/Modal/Modal';
import shutdownIcon from '../../images/shutdown.svg';
import { useToastService } from '../toast/ToastService';
import { usePermissionModal } from '../../features/common/PermissionModal/PermissionModalService';
import { useParams } from 'react-router-dom';
import { R, useRBAC, WIDGETS } from '../../rbac';
import { showWarningToast } from '../toast/toastBridge';
import { formatDate } from '../../utilities/dateHelpers';

const TaskDetailsWidget = ({
  taskRefreshTrigger,
  handleApplyClickedTaskWidget,
  wellId,
  dashboardName,
  isRenderFromTaskTab = false,
}: {
  taskRefreshTrigger: boolean;
  handleApplyClickedTaskWidget: (columns: any[], isConfigCheckBoxClicked: boolean) => void;
  wellId: number | undefined;
  dashboardName: string;
  isRenderFromTaskTab?: boolean;
}) => {
  const resource = R.widget(dashboardName, WIDGETS.TASK_DETAILS);
  const { can } = useRBAC();
  const rPermission = {
    canExport: can(resource, 'export'),
    canConfigureColumns: can(resource, 'configureColumns'),
    canAdd: can(resource, 'add'),
    canEdit: can(resource, 'edit'),
    canDelete: can(resource, 'delete'),
    canMarkAsComplete: can(resource, 'markAsComplete'),
  };

  const wellTestData = useAppSelector((state) => state.WellTestIdSlice?.data);
  const [isTaskTableInitialized, setIsTaskTableInitialized] = useState(false);
  const [taskData, setTaskData] = useState<TaskData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [fixedColumns, setFixedColumns] = useState<string[]>([]);
  const [allColumnsForToolbar, setAllColumnsForToolbar] = useState<string[]>([]);
  // const [columnDefs, setColumnDefs] = useState<any[]>([]);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskData | null>(null);
  const [selectedTaskToDelete, setDeleteTask] = useState<TaskData | null>(null);
  const [isDeleteTaskModalOpen, setIsDeleteTaskModalOpen] = useState(false);
  const [selectedTaskToMarkAsComplete, setMarkAsCompleteTask] = useState<TaskData | null>(null);
  const [isMarkAsCompleteTaskModalOpen, setIsMarkAsCompleteTaskModalOpen] = useState(false);
  const [applyTrigger, setApplyTrigger] = useState(false);
  const [isConfigCheckBoxClicked, setsConfigCheckBoxClicked] = useState(false);
  const { showSuccessToast, showErrorToast } = useToastService();
  const [updatedColumns, setUpdatedColumns] = useState<any[]>([]);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [taskWidgetProps, setTaskWidgetProps] = useState<{ columns?: any[]; fixedColumns?: any[] }>({});
  const [processedTaskData, setProcessedTaskData] = useState<TaskData[]>([]);
  const { assetId } = useParams<{ assetId: string }>();
  const { showPermissionModal } = usePermissionModal();
  const [isLoading, setIsLoading] = useState(false);
  const columnDefs = [
    { Header: '', id: 'actions' },
    { Header: 'Task no.', accessor: 'TaskNumber' },
    { Header: 'Category', accessor: 'CategoryName' },
    { Header: 'Task', accessor: 'Task' },
    { Header: 'Task comment', accessor: 'Comment' },
    { Header: 'Created date', accessor: 'Created' },
    { Header: 'Due date', accessor: 'DueDate' },
    { Header: 'Assigned to', accessor: 'AssignedTo' },
    { Header: 'Created by', accessor: 'CreatedBy' },
    { Header: 'Completed', accessor: 'CompletedDate' },
  ];
  const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const taskWidget = pumpCheckerPageData?.find((obj: any) => obj.key === 'taskDetails');
  useEffect(() => {
    const props = taskWidget?.widgetProperty?.widgetProperties || {};
    setTaskWidgetProps(props);
    const fixed = ['actions', ...(props.fixedColumns?.map((col: any) => col.key).filter(Boolean) || [])];
    const visible = props.columns?.filter((col: any) => col.selected).map((col: any) => col.key) || [];

    const all = props.columns?.map((col: any) => col.key) || [];
    setFixedColumns(fixed);
    setVisibleColumns(visible);
    setAllColumnsForToolbar(all);
    setIsTaskTableInitialized(true);
    setApplyTrigger(true);
    setTimeout(() => setApplyTrigger(false), 300);
  }, [taskWidget, assetId]);

  useEffect(() => {
    if (wellId) {
      setIsLoading(true);
      fetchTaskData(wellId.toString()).then((data) => {
        setTaskData(data);
        setIsLoading(false);
      });
    } else {
      setTaskData([]);
    }
  }, [wellId, taskRefreshTrigger]);

  const getColumnHeader = (columnId: string) => {
    const searchColumnId = columnId === 'DueDateString' ? 'DueDate' : columnId;
    const col = columnDefs.find((col: any) => col.accessor === searchColumnId || col.id === searchColumnId);
    return col?.Header || columnId;
  };

  const handleUpdateColumns = (updated: { id: string; Header: string; isVisible: boolean }[]) => {
    const newVisible = updated.filter((col) => col.isVisible).map((col) => col.id);
    setUpdatedColumns(updated);
    setVisibleColumns(newVisible);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns((prev) =>
      prev.includes(columnId) ? prev.filter((col) => col !== columnId) : [...prev, columnId],
    );
  };

  const resetHandler = () => {
    if (!taskWidgetProps?.columns?.length) return;

    setIsResetClicked(true);
    const visibleColumnsAfterReset = taskWidgetProps.columns.map((col: any) => col.key);

    setAllColumnsForToolbar(visibleColumnsAfterReset);
    setVisibleColumns(visibleColumnsAfterReset);

    setTimeout(() => {
      setIsResetClicked(false);
    }, 3000);
  };

  const handleSearch = (term: string) => setSearchTerm(term);

  const handleApplyClicked = () => {
    const visibleKeys = visibleColumns;
    const transformedArray = updatedColumns.map((item, index) => ({
      order: index + 1,
      label: item.Header,
      key: item.id,
      selected: visibleKeys ? visibleKeys.includes(item.key) : [],
    }));
    handleApplyClickedTaskWidget(transformedArray, isConfigCheckBoxClicked);
    setApplyTrigger(true);
    setTimeout(() => setApplyTrigger(false), 300);
  };

  const handleSaveConfig = (config: boolean) => {
    setsConfigCheckBoxClicked(config);
  };

  const onSaveColumns = () => {
    console.log('saved Columns');
  };
  // Function to handle exporting table content to XLSX
  const handleExportXLS = () => {
    if (!visibleColumns.length) {
      console.warn('No columns selected for export');
      return;
    }
    try {
      const exportData = processedTaskData.map((task) => {
        const row: Record<string, any> = {};
        visibleColumns.forEach((col) => {
          switch (col) {
            case 'TaskNumber':
              row['Task number'] = task.TaskNumber || '';
              break;
            case 'CategoryName':
              row['Category'] = task.CategoryName || '';
              break;
            case 'Task':
              row['Task'] = task.Task || '';
              break;
            case 'Comment':
              row['Comment'] = task.Comment || '';
              break;
            case 'Created':
              row['Created date'] = task.Created ? formatDateTime(task.Created) : '';
              break;
            case 'DueDateString':
              row['Due date'] = formatDate(task.DueDate);
              break;
            case 'AssignedTo':
              row['Assigned to'] = task.AssignedTo;
              break;
            case 'CreatedBy':
              row['Created by'] = task.CreatedBy;
              break;
            case 'CompletedDate':
              row['Completed'] = task.CompletedDate ? formatDateTime(task.CompletedDate) : '';
              break;
            default:
              row[col] = task[col as keyof TaskData] ?? '';
          }
        });
        return row;
      });

      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0].replace(/-/g, '');
      const fileName = `Well-Tasks-${wellTestData?.Name}-${formattedDate}.xlsx`;

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Error exporting to XLSX:', error);
    }
  };

  // Utility function for consistent date formatting
  const formatDateTime = (dateString: string): string => {
    return formatDate(dateString, true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTaskToDelete || !wellId) return;
    const success = await deleteTask(selectedTaskToDelete);
    if (success) {
      showSuccessToast('Task deleted successfully');
      setIsLoading(true);
      const updated = await fetchTaskData(wellId.toString());
      setTaskData(updated);
      setIsLoading(false);
    } else {
      showErrorToast('Failed to delete task', 'There was an error deleting the task. Please try again.');
    }
    setIsDeleteTaskModalOpen(false);
    setDeleteTask(null);
  };

  const handleMarkAsCompleteConfirm = async () => {
    if (!rPermission.canMarkAsComplete) {
      showWarningToast('You do not have permission to mark tasks as complete.');
      return;
    }
    if (!selectedTaskToMarkAsComplete || !wellId) return;
    const success = await markAsCompleteTaskSave(selectedTaskToMarkAsComplete.WellTaskId);
    if (success) {
      showSuccessToast('Task completed successfully');
      setIsLoading(true);
      const updated = await fetchTaskData(wellId.toString());
      setTaskData(updated);
      setIsLoading(false);
    } else {
      showErrorToast('Failed to mark task as complete', 'There was an error completing the task. Please try again');
    }
    setIsMarkAsCompleteTaskModalOpen(false);
    setMarkAsCompleteTask(null);
  };

  const addTaskClickHandler = () => {
    setEditingTask(null);
    setIsAddTaskModalOpen(true);
  };

  const editTaskClickHandler = (task: TaskData) => {
    setEditingTask(task);
    setIsAddTaskModalOpen(true);
  };

  const deleteTaskClickHandler = (task: TaskData) => {
    setDeleteTask(task);
    setIsDeleteTaskModalOpen(true);
  };

  const markAsCompleteTaskClickHandler = (task: TaskData) => {
    if (rPermission.canMarkAsComplete) {
      setMarkAsCompleteTask(task);
      setIsMarkAsCompleteTaskModalOpen(true);
    } else {
      showPermissionModal('You do not have permission to mark tasks as complete.');
    }
  };

  return (
    <main className='main-container table-wrap tasknotes-dashboard' style={{ zIndex: 'auto', minHeight: isRenderFromTaskTab ? 'calc(96vh - 250px)' : '450px' }}>
      <TableHeader
        title={
          wellTestData?.Name && wellTestData?.Identifier
            ? `Task details - ${wellTestData.Name} / ${wellTestData.Identifier}`
            : 'Task details'
        }
        buttonLabel='Add task'
        onAddTestClick={() => {
          addTaskClickHandler();
        }}
        rPermission={rPermission}
        tableName='taskDetail'
      />
      <TableToolbar
        tableName='taskDetails'
        disableAllInstallations={true}
        columns={allColumnsForToolbar
          .filter((id) => !fixedColumns.includes(id))
          .map((id) => ({
            id,
            Header: getColumnHeader(id),
            isVisible: visibleColumns.includes(id),
          }))}
        onColumnToggle={handleColumnToggle}
        onSaveColumns={onSaveColumns}
        onSearch={handleSearch}
        onUpdateColumns={handleUpdateColumns}
        onApplyClicked={() => handleApplyClicked()}
        saveConfigHasbeenClicked={handleSaveConfig}
        onExportXLS={handleExportXLS}
        resetToDefaultSystemClicked={() => {
          resetHandler();
        }}
        isTaskTableInitialized={isTaskTableInitialized}
        ref={undefined}
        rPermission={rPermission}
      />

      {[...fixedColumns, ...visibleColumns].length > 0 && (
        <TaskDataTable
          visibleColumns={[...fixedColumns, ...visibleColumns]}
          searchTerm={searchTerm}
          taskData={taskData}
          onEditTask={(task) => {
            editTaskClickHandler(task);
          }}
          onDeleteTask={(task) => {
            deleteTaskClickHandler(task);
          }}
          onMarkAsCompleteTask={(task) => {
            markAsCompleteTaskClickHandler(task);
          }}
          onUpdateVisibleColumns={setVisibleColumns}
          applyTrigger={applyTrigger}
          isConfigCheckBoxEnabled={isConfigCheckBoxClicked}
          isResetClicked={isResetClicked}
          onFilteredSortedDataReady={setProcessedTaskData}
          rPermission={rPermission}
          isRenderFromTaskTab={isRenderFromTaskTab}
          isLoading={isLoading}

        />
      )}

      {isAddTaskModalOpen && (
        <AddTaskModal
          isOpen={isAddTaskModalOpen}
          onClose={() => {
            setIsAddTaskModalOpen(false);
            setEditingTask(null);
          }}
          taskToEdit={editingTask}
          onTaskSaved={() => wellId && fetchTaskData(wellId.toString()).then(setTaskData)}
        />
      )}

      <Modal
        open={isDeleteTaskModalOpen}
        setOpen={setIsDeleteTaskModalOpen}
        message='Are you sure you want to delete this task ?'
        buttonText='Yes'
        callbackOnConfirm={handleDeleteConfirm}
        title='Delete task'
        Icon={shutdownIcon}
        buttonColor='red'
      />

      <Modal
        open={isMarkAsCompleteTaskModalOpen}
        setOpen={setIsMarkAsCompleteTaskModalOpen}
        message='Are you sure you want to mark this task as completed ?'
        buttonText='Yes'
        callbackOnConfirm={handleMarkAsCompleteConfirm}
        title='Complete task'
        Icon={shutdownIcon}
        buttonColor='red'
      />
    </main>
  );
};

export default TaskDetailsWidget;
